import type { LiveOpenShipment, LiveRefundOwed, LiveShipmentAddress } from "@hobbyshopify/backend-client";
import { useObserver, useSubtree } from "subtree.js/react";
import { LoadFailed, Loading } from "../../components/LoadState";
import { formatCents } from "../../core/primitives/live";
import { type AddressDraft, SHIPPING_FILTERS, ShippingActions, ShippingState, shipmentMatches } from "./shipping-model";

/**
 * The Shipping tab: one parcel per buyer, filled as their boxes are broken,
 * and the money owed back. Not in the cockpit: shipping spans every stream.
 */

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  AWAITING_ADDRESS: { label: "Needs address", className: "badge-warn" },
  AWAITING_PAYMENT: { label: "Ready to label", className: "badge-neutral" },
  PAID: { label: "Ready to label", className: "badge-neutral" },
  LABEL_CREATED: { label: "Label bought", className: "badge-accent" },
  SHIPPED: { label: "Posted", className: "badge-accent" },
  DELIVERED: { label: "Delivered", className: "badge-accent" },
  CANCELLED: { label: "Cancelled", className: "badge-danger" },
};

const formatAddress = (a: LiveShipmentAddress) =>
  [a.name, a.street1, a.street2, [a.city, a.state, a.zip].filter(Boolean).join(" "), a.country]
    .filter(Boolean)
    .join(", ");

function AddressForm() {
  const state = useSubtree(ShippingState);
  const actions = useSubtree(ShippingActions);
  const observer = useObserver();
  const form = observer.watch(state.address);
  const messages = observer.watch(state.addressMessages);
  const save = observer.watch(state.addressSave);

  const field = (key: keyof AddressDraft, label: string, extra?: { required?: boolean; width?: string }) => (
    <label className={`block ${extra?.width ?? ""}`}>
      <span className="label">{label}</span>
      <input
        className="field"
        value={form[key]}
        required={extra?.required}
        onChange={(e) => actions.setAddressField(key, e.target.value)}
      />
    </label>
  );

  return (
    <form
      className="mt-2.5 space-y-2.5 rounded-row border border-line p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void actions.saveAddress();
      }}
    >
      {field("name", "Name", { required: true })}
      {field("street1", "Street address", { required: true })}
      {field("street2", "Apartment, suite (optional)")}
      <div className="grid grid-cols-2 gap-2.5">
        {field("city", "City", { required: true })}
        {field("state", "State / province")}
        {field("zip", "Postal code")}
        {field("country", "Country code")}
      </div>
      {field("phone", "Phone (optional)")}

      {messages.length > 0 && (
        <div className="note note-warn">
          {messages.map((m) => (
            <p key={m}>{m}</p>
          ))}
          <p className="mt-1 opacity-70">Saved anyway — the carrier may still deliver it.</p>
        </div>
      )}
      {save.error && <p className="note note-danger">{save.error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary btn-sm" onClick={actions.closeAddress}>
          {messages.length > 0 ? "Done" : "Cancel"}
        </button>
        <button type="submit" className="btn-primary btn-sm" disabled={save.busy}>
          {save.busy ? "Pricing…" : "Save address"}
        </button>
      </div>
    </form>
  );
}

/** The slip that goes in the envelope, fetched fresh. */
function PackingSlipModal() {
  const state = useSubtree(ShippingState);
  const actions = useSubtree(ShippingActions);
  const observer = useObserver();
  const status = observer.watch(state.slipStatus);
  const slip = observer.watch(state.slip);

  return (
    <div className="scrim items-center justify-center p-4">
      <div
        className="modal max-h-[85vh] w-full max-w-md overflow-y-auto p-5"
        role="dialog"
        aria-modal="true"
        aria-label="Packing slip"
      >
        {status.kind === "loading" && <p className="text-sm text-muted">Loading…</p>}
        {status.kind === "error" && <p className="note note-danger">{status.message}</p>}

        {slip && (
          <div className="space-y-3">
            <div>
              {slip.locationName && <p className="text-sm text-muted">From {slip.locationName}</p>}
              <h3 className="text-lg font-bold text-white">{slip.buyerName}</h3>
            </div>

            <p className="text-sm leading-snug text-muted">
              {slip.address ? formatAddress(slip.address) : "No address yet"}
            </p>

            <ul className="space-y-1.5 border-t border-line pt-3">
              {slip.lines.map((line) => (
                <li
                  key={`${line.breakId ?? ""}:${line.slotNumber ?? ""}:${line.title}:${line.detail}`}
                  className="text-sm"
                >
                  <span className="font-medium text-white">{line.title}</span>
                  {line.detail && <span className="text-muted"> — {line.detail}</span>}
                  {line.sellerNote && <p className="text-xs text-muted">Note: {line.sellerNote}</p>}
                </li>
              ))}
            </ul>

            <p className="border-t border-line pt-3 text-sm">
              <span className="text-muted">Paid </span>
              <span className="font-medium text-white">{formatCents(slip.totalPaidCents)}</span>
            </p>

            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary btn-sm" onClick={actions.closeSlip}>
                Close
              </button>
              <button type="button" className="btn-primary btn-sm" onClick={() => window.print()}>
                Print
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RefundsOwed({ refunds }: { refunds: readonly LiveRefundOwed[] }) {
  const failed = refunds.filter((r) => r.reason === "refund_failed");

  return (
    <div className={`space-y-2 rounded-row border border-line p-3 ${failed.length > 0 ? "note-danger" : ""}`}>
      <p className="text-sm font-medium text-white">Refunds owed</p>
      <p className="text-xs text-muted">
        {failed.length > 0
          ? "Some refunds did not go through. These buyers have been charged for cards they will not receive."
          : "Buyers who paid into a break that was called off. Refund them in Stripe."}
      </p>
      <ul className="space-y-2">
        {refunds.map((r) => (
          <li
            key={`${r.breakId}-${r.buyerUserId}-${r.reason}`}
            className="flex items-center gap-3 rounded-row border border-line px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-white">{r.buyerName}</p>
              <p className="text-xs text-muted">
                {r.breakTitle} — {r.orderIds.length} order{r.orderIds.length === 1 ? "" : "s"}
              </p>
            </div>
            <span className="text-sm font-medium text-white">{formatCents(r.amountCents)}</span>
            <span className={`badge shrink-0 ${r.reason === "refund_failed" ? "badge-danger" : "badge-warn"}`}>
              {r.reason === "refund_failed" ? "Refund failed" : "Break cancelled"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ShipmentRow({ shipment, carrierShipping }: { shipment: LiveOpenShipment; carrierShipping: boolean }) {
  const state = useSubtree(ShippingState);
  const actions = useSubtree(ShippingActions);
  const observer = useObserver();
  const editing = observer.watch(state.editing) === shipment.shipmentId;
  const slipOpen = observer.watch(state.slipShipmentId) === shipment.shipmentId;
  const rowBusy = observer.watch(state.rowBusy);
  const rowError = observer.watch(state.rowError);
  const askPhone = observer.watch(state.phonePrompt) === shipment.shipmentId;
  const senderPhone = observer.watch(state.senderPhone);

  const status = shipment.status;
  const badge = STATUS_BADGE[status] ?? null;
  const address = shipment.address;
  const labelBought = status === "LABEL_CREATED" || status === "SHIPPED" || status === "DELIVERED";
  const canShip = status === "LABEL_CREATED" || status === "PAID" || status === "AWAITING_PAYMENT";
  const buying = rowBusy?.shipmentId === shipment.shipmentId && rowBusy.move === "label";
  const posting = rowBusy?.shipmentId === shipment.shipmentId && rowBusy.move === "shipped";
  const failure = rowError?.shipmentId === shipment.shipmentId ? rowError.message : null;

  return (
    <li className="rounded-row border border-line p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-white">{shipment.buyerName}</p>
          <p className="text-xs text-muted">
            {shipment.lines.length} spot{shipment.lines.length === 1 ? "" : "s"} —{" "}
            {formatCents(shipment.totalPaidCents)}
          </p>
        </div>
        {badge && <span className={`badge shrink-0 ${badge.className}`}>{badge.label}</span>}
      </div>

      <ul className="mt-2 space-y-0.5">
        {shipment.lines.map((line) => (
          <li key={line.slotId} className="text-xs text-muted">
            <span className="text-white">{line.assignedLabel ?? "not yet assigned"}</span> — {line.breakTitle}, spot{" "}
            {line.slotNumber}
            {line.sellerNote && <> · {line.sellerNote}</>}
          </li>
        ))}
      </ul>

      {address && !editing && (
        <p className="mt-2 text-xs leading-snug text-muted">
          {formatAddress(address)}
          {shipment.carrier && shipment.shippingCostCents !== null && (
            <>
              {" · "}
              {shipment.carrier} {shipment.service} {formatCents(shipment.shippingCostCents)}
            </>
          )}
        </p>
      )}

      {shipment.trackingNumber && (
        <p className="mt-1 text-xs text-muted">
          Tracking{" "}
          {shipment.trackingUrl ? (
            <a href={shipment.trackingUrl} target="_blank" rel="noreferrer" className="underline">
              {shipment.trackingNumber}
            </a>
          ) : (
            shipment.trackingNumber
          )}
        </p>
      )}

      {editing && <AddressForm />}

      {failure && <p className="note note-danger mt-2">{failure}</p>}

      {askPhone && !editing && (
        <form
          className="mt-2"
          onSubmit={(e) => {
            e.preventDefault();
            void actions.buyLabel(shipment.shipmentId);
          }}
        >
          <label className="block">
            <span className="label">Shop phone — saved on your ship-from location</span>
            <input
              className="field"
              type="tel"
              autoComplete="tel"
              required
              value={senderPhone}
              onChange={(e) => actions.setSenderPhone(e.target.value)}
            />
          </label>
        </form>
      )}

      {!editing && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <button type="button" className="btn-secondary btn-sm" onClick={() => actions.editAddress(shipment)}>
            {address ? "Edit address" : "Add address"}
          </button>

          {carrierShipping && address && !labelBought && (
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={buying || (askPhone && !senderPhone.trim())}
              onClick={() => actions.buyLabel(shipment.shipmentId)}
            >
              {buying ? "Buying…" : "Buy label"}
            </button>
          )}

          {shipment.labelUrl && (
            <a href={shipment.labelUrl} target="_blank" rel="noreferrer" className="btn-secondary btn-sm">
              Print label
            </a>
          )}

          {canShip && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={posting}
              onClick={() => actions.markPosted(shipment.shipmentId)}
            >
              {posting ? "Saving…" : "Mark posted"}
            </button>
          )}

          <button type="button" className="btn-ghost" onClick={() => actions.openSlip(shipment.shipmentId)}>
            Packing slip
          </button>
        </div>
      )}

      {slipOpen && <PackingSlipModal />}
    </li>
  );
}

export function ShippingPage() {
  const state = useSubtree(ShippingState);
  const actions = useSubtree(ShippingActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const shipments = observer.watch(state.shipments);
  const refunds = observer.watch(state.refundsOwed);
  const carrierShipping = observer.watch(state.carrierShipping);
  const shippingNote = observer.watch(state.shippingNote);
  const filter = observer.watch(state.filter);
  const csv = observer.watch(state.csv);

  const nothing = shipments.length === 0 && refunds.length === 0;
  const loading = status.kind === "loading" && nothing;
  const shown = shipments.filter((s) => shipmentMatches(s, filter));

  return (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-8 sm:px-6 lg:px-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-white">Shipping</h1>
          <p className="text-sm text-muted">
            When a box is broken, each buyer's teams go into their parcel, from every stream. A parcel takes more until
            you buy its label or mark it posted.
          </p>
        </div>
        {!nothing && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={csv.busy}
            onClick={() => void actions.exportCsv()}
          >
            {csv.busy ? "Building…" : "Export CSV"}
          </button>
        )}
      </header>

      {!carrierShipping && shippingNote && <p className="note">{shippingNote}</p>}
      {csv.error && <p className="note note-danger">{csv.error}</p>}

      {loading ? (
        <Loading label="Loading your parcels…" />
      ) : status.kind === "error" ? (
        <LoadFailed title="Couldn't load shipping." message={status.message} onRetry={() => void actions.reload()} />
      ) : nothing ? (
        <p className="py-10 text-center text-sm text-muted">
          Nothing to ship. Spots appear here once their box is broken.
        </p>
      ) : (
        <>
          {refunds.length > 0 && <RefundsOwed refunds={refunds} />}

          {shipments.length > 0 && (
            <section className="panel space-y-3 p-4">
              <h2 className="font-semibold text-white">Parcels</h2>
              <div className="flex flex-wrap gap-1">
                {SHIPPING_FILTERS.map((tab) => (
                  <button
                    type="button"
                    key={tab.key}
                    onClick={() => actions.setFilter(tab.key)}
                    className={`rounded-btn px-3 py-1.5 text-xs font-medium transition-colors ${
                      filter === tab.key
                        ? "bg-accent text-ink"
                        : "bg-surface text-muted hover:bg-surface-strong hover:text-white"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {shown.length === 0 ? (
                <p className="text-sm text-muted">Nothing here.</p>
              ) : (
                <ul className="space-y-2.5">
                  {shown.map((shipment) => (
                    <ShipmentRow key={shipment.shipmentId} shipment={shipment} carrierShipping={carrierShipping} />
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
